import { useLanguage } from "../../contexts/LanguageContext";
import { formatValueByUnit } from "../../lib/coachUtils";
import { levelProgress } from "../../lib/performanceModelUtils";

const UNIT_SUFFIX = { kg: "kg", reps: "reps", meters: "m", points: "pt", hit_factor: "HF" };

function formatAmount(value, unit, language) {
	if (unit === "minutes_seconds" || unit === "seconds") {
		return formatValueByUnit(Math.round(Math.abs(value)), "minutes_seconds");
	}
	const number = Math.abs(value).toLocaleString(language === "en" ? "en-US" : "it-IT", {
		maximumFractionDigits: 1,
	});
	const suffix = UNIT_SUFFIX[unit];
	return suffix ? `${number} ${suffix}` : number;
}

/**
 * Il livello di un esercizio nel profilo prestativo: il punteggio con il nome
 * del livello, la barra con le tacche della scala e il prossimo traguardo.
 *
 * Prima il profilo mostrava solo il massimale: il livello si leggeva soltanto
 * nel radar. Calcoli in `levelProgress` (lib/performanceModelUtils.js); niente
 * se l'esercizio non ha una tabella.
 *
 * Props: exercise (una riga del profilo), domain (per scala e nomi), color.
 */
export function LevelBar({ exercise, domain, color }) {
	const { t, language } = useLanguage();
	const progress = levelProgress(exercise, domain);
	if (!progress) return null;
	const locale = language === "en" ? "en-US" : "it-IT";
	const score = progress.score.toLocaleString(locale, { maximumFractionDigits: 1 });
	const { next } = progress;

	return (
		<div className="mt-2 space-y-1.5" data-testid="pp-level">
			<div className="flex items-baseline justify-between gap-2 min-w-0">
				<p className="text-sm min-w-0 truncate">
					<span className="font-heading" style={{ color }}>
						{t("ppLevel").replace("{score}", score)}
					</span>
					{progress.label && (
						<span className="text-muted-foreground"> · {progress.label}</span>
					)}
				</p>
				<span className="text-[10px] text-muted-foreground shrink-0">
					{t("ppLevelOf").replace("{max}", String(progress.max))}
				</span>
			</div>
			<div
				className="relative h-2 rounded-full bg-secondary"
				role="meter"
				aria-valuemin={0}
				aria-valuemax={progress.max}
				aria-valuenow={progress.score}
				aria-label={t("ppLevel").replace("{score}", score)}
			>
				<div
					className="absolute inset-y-0 left-0 rounded-full"
					style={{ width: `${progress.percent}%`, backgroundColor: color }}
				/>
				{progress.ticks.map((tick) => (
					<span
						key={tick.score}
						className={`absolute top-0 h-2 w-px ${
							tick.reached ? "bg-background/60" : "bg-muted-foreground/40"
						}`}
						style={{ left: `${tick.percent}%` }}
					/>
				))}
			</div>
			<p className="text-[11px] text-muted-foreground">
				{next
					? t("ppNextLevel")
							.replace(
								"{delta}",
								`${progress.lowerIsBetter ? "−" : "+"}${formatAmount(next.delta, exercise.unit, language)}`,
							)
							.replace("{score}", String(next.score))
							.replace("{label}", next.label ? ` · ${next.label}` : "")
							.replace("{value}", formatAmount(next.value, exercise.unit, language))
					: t("ppTopLevel")}
			</p>
		</div>
	);
}
